import { HttpBackend, HttpClient } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { connectAuthEmulator, getAuth, provideAuth } from '@angular/fire/auth';
import { connectStorageEmulator, getStorage, provideStorage } from '@angular/fire/storage';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as Sentry from '@sentry/angular';

import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { Language, defaultLanguage, loadLanguage, supportedLanguages } from './core/i18n/i18n';
import { InterceptorModule } from './interceptors/interceptor.module';

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    InterceptorModule,
    TranslateModule.forRoot({
      defaultLanguage: defaultLanguage,
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpBackend],
      },
    }),
    CoreModule,
    AppRoutingModule,
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => {
      const auth = getAuth();

      if (environment.useFirebaseEmulator ?? false) {
        connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
      }

      return getAuth();
    }),
    provideStorage(() => {
      const storage = getStorage();

      if (environment.useFirebaseEmulator ?? false) {
        connectStorageEmulator(storage, 'http://localhost', 9199);
      }

      return getStorage();
    }),
  ],
  bootstrap: [AppComponent],
  providers: [
    ...(environment.sentryDsn
      ? [
          {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
          },
          {
            provide: Sentry.TraceService,
            deps: [Router],
          },
          {
            provide: APP_INITIALIZER,
            useFactory: () => () => ({}),
            deps: [Sentry.TraceService],
            multi: true,
          },
        ]
      : []),
  ],
})
export class AppModule {
  constructor(private translateService: TranslateService) {
    this.initTranslateService();
  }

  private initTranslateService() {
    this.translateService.langs = supportedLanguages;
    const browserLang = this.translateService.getBrowserLang() ?? defaultLanguage;
    const defaultLang = supportedLanguages.includes(browserLang as Language) ? browserLang : defaultLanguage;
    const language = loadLanguage() ?? defaultLang;
    // if user is authenticated, this might be overridden with the language stored in the user's profile.
    this.translateService.use(language);
  }
}

// AoT requires an exported function for factories
export function httpLoaderFactory(http: HttpBackend) {
  const client = new HttpClient(http);
  return new TranslateHttpLoader(client);
}
